import React, { useState, useEffect } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useTheme,
    useMediaQuery,
    Typography,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Grid,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Divider,
    Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { OgrenciError } from '../../../types/ErrorModals/OgrenciError';
import { getOgrenciler } from '../../../services/api/ogrenciService';
import { OgrenciResponseModal } from '../../../types/modals'; 
import OdemeDialogModal from './OdemeDialogModal';
import TaksitPlanDialogModal from './TaksitPlanDialogModal';
import OgrenciEkleDialogModal from './OgrenciEkleDialogModal';

const OgrenciMuhasebeIslemleri: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { kullaniciModal } = useAuth();
    
    // Sezon state'i
    const [selectedSezon, setSelectedSezon] = useState<number>(2025);
    
    // Filtre state'leri
    const [searchText, setSearchText] = useState<string>('');
    const [filterField, setFilterField] = useState<string>('adiSoyadi');
    const [filteredData, setFilteredData] = useState<OgrenciResponseModal[]>([]);
    
    // Ödeme dialog state'leri
    const [odemeDialogOpen, setOdemeDialogOpen] = useState<boolean>(false);
    const [selectedOgrenci, setSelectedOgrenci] = useState<OgrenciResponseModal | null>(null);
    
    // Taksit planı dialog state'leri
    const [taksitPlanDialogOpen, setTaksitPlanDialogOpen] = useState<boolean>(false);
    
    // Öğrenci ekleme dialog state'i
    const [ogrenciEkleDialogOpen, setOgrenciEkleDialogOpen] = useState<boolean>(false);
    
    // Sezon değişikliğini yönetmek için handler
    const handleSezonChange = (event: SelectChangeEvent<number>) => {
        setSelectedSezon(event.target.value as number);
    };
    
    // Arama metni değişikliğini yönetmek için handler
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
    };
    
    // Filtre alanı değişikliğini yönetmek için handler
    const handleFilterFieldChange = (event: SelectChangeEvent<string>) => {
        setFilterField(event.target.value);
    };
    
    // Arama metnini temizlemek için handler
    const handleClearSearch = () => {
        setSearchText('');
    };
    
    // Ödeme dialogunu açmak için handler
    const handleOpenOdemeDialog = (ogrenci: OgrenciResponseModal) => {
        setSelectedOgrenci(ogrenci);
        setOdemeDialogOpen(true);
    };
    
    // Ödeme dialogunu kapatmak için handler
    const handleCloseOdemeDialog = () => {
        setOdemeDialogOpen(false);
        setSelectedOgrenci(null);
    };
    
    // Taksit planı dialogunu açmak için handler
    const handleOpenTaksitPlanDialog = (ogrenci: OgrenciResponseModal) => {
        setSelectedOgrenci(ogrenci);
        setTaksitPlanDialogOpen(true);
    };
    
    // Taksit planı dialogunu kapatmak için handler
    const handleCloseTaksitPlanDialog = () => {
        setTaksitPlanDialogOpen(false);
        setSelectedOgrenci(null);
    };
    
    // Öğrenci ekleme dialogunu açmak için handler
    const handleOpenOgrenciEkleDialog = () => {
        setOgrenciEkleDialogOpen(true);
    };
    
    // Öğrenci ekleme dialogunu kapatmak için handler
    const handleCloseOgrenciEkleDialog = () => {
        setOgrenciEkleDialogOpen(false);
        // Öğrenci eklendiğinde listeyi yenile
        refetch();
    };
    
    // Mevcut yıl ve gelecek birkaç yıl için sezon seçenekleri
    const currentYear = new Date().getFullYear();
    const sezonOptions = [ 
        currentYear - 1,
        currentYear,
        currentYear + 1
    ];
    
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['ogrenciler', kullaniciModal?.id, selectedSezon],
        queryFn: async () => {
            try {
                return await getOgrenciler({
                    kurumKodu: localStorage.getItem('kurumKodu') || '', 
                    sezon: selectedSezon
                });
            } catch (err) {
                const ogrenciError = err as OgrenciError;
                throw new Error(ogrenciError.message);
            }
        },
        enabled: !!kullaniciModal?.id,
        retry: 1
    });
    
    // Veri filtreleme
    useEffect(() => {
        if (!data) return;
        
        if (!searchText.trim()) {
            setFilteredData(data);
            return;
        }
        
        const lowercaseSearchText = searchText.toLowerCase().trim();
        
        const filtered = data.filter(ogrenci => {
            switch (filterField) {
                case 'adiSoyadi':
                    return ogrenci.adiSoyadi.toLowerCase().includes(lowercaseSearchText);
                case 'no':
                    return ogrenci.no.toLowerCase().includes(lowercaseSearchText);
                case 'sinif':
                    return ogrenci.sinif.toLowerCase().includes(lowercaseSearchText);
                default:
                    return ogrenci.adiSoyadi.toLowerCase().includes(lowercaseSearchText);
            }
        });
        
        setFilteredData(filtered);
    }, [data, searchText, filterField]);
    
    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }
    
    if (error) {
        return (
            <Alert
                severity="error"
                sx={{ mt: 2 }}
                action={
                    process.env.NODE_ENV === 'development' && (
                        <Typography variant="caption" display="block">
                            CorrelationId: {(error as OgrenciError).correlationId}
                        </Typography>
                    )
                }
            >
                {(error as Error).message}
            </Alert>
        );
    }
    
    return (
        <Box sx={{ mt: { xs: 2, sm: 3 } }}>
            {/* Sezon seçimi ve filtre */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="sezon-select-label">Sezon</InputLabel>
                            <Select
                                labelId="sezon-select-label"
                                id="sezon-select"
                                value={selectedSezon}
                                label="Sezon"
                                onChange={handleSezonChange}
                            >
                                {sezonOptions.map((year) => (
                                    <MenuItem key={year} value={year}>
                                        {year} - {year + 1} Eğitim Yılı
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item>
                        <Button 
                            variant="contained" 
                            color="primary"
                            onClick={() => refetch()}
                            startIcon={<FilterListIcon />}
                        >
                            Listele
                        </Button>
                    </Grid>
                    <Grid item sx={{ ml: 'auto' }}>
                        <Button 
                            variant="contained" 
                            color="success"
                            onClick={handleOpenOgrenciEkleDialog}
                            startIcon={<PersonAddIcon />}
                        >
                            Yeni Öğrenci Ekle
                        </Button>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="filter-field-label">Filtre Alanı</InputLabel>
                            <Select
                                labelId="filter-field-label"
                                id="filter-field-select"
                                value={filterField}
                                label="Filtre Alanı"
                                onChange={handleFilterFieldChange}
                            >
                                <MenuItem value="adiSoyadi">Adı Soyadı</MenuItem>
                                <MenuItem value="no">Öğrenci No</MenuItem>
                                <MenuItem value="sinif">Sınıf</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Ara"
                            variant="outlined"
                            value={searchText}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: searchText && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="clear search"
                                            onClick={handleClearSearch}
                                            edge="end"
                                            size="small"
                                        >
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            placeholder={`${filterField === 'adiSoyadi' ? 'Öğrenci adı' : filterField === 'no' ? 'Öğrenci no' : 'Sınıf'} ile ara...`}
                        />
                    </Grid>
                </Grid>
            </Paper>
            
            {/* Öğrenci tablosu */}
            {!data?.length ? (
                <Alert severity="info">
                    Seçilen sezon için kayıtlı öğrenci bulunmamaktadır.
                </Alert>
            ) : filteredData.length === 0 ? (
                <Alert severity="info">
                    Arama kriterlerine uygun öğrenci bulunamadı.
                </Alert>
            ) : (
                <Box sx={{ overflowX: "auto" }}>
                    <TableContainer component={Paper} sx={{ minWidth: isMobile ? 600 : 800 }}>
                        <Table aria-label="öğrenci listesi tablosu">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                                        Öğrenci No
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                                        Adı Soyadı
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                                        Sınıf
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                                        Kayıt Tarihi
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                                        Sezon
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                                        İşlemler
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.map((ogrenci, index) => (
                                    <TableRow
                                        key={ogrenci.recNo || index}
                                        sx={{
                                            "&:nth-of-type(odd)": { backgroundColor: "#f9f9f9" },
                                            "&:hover": {
                                                backgroundColor: "rgba(232, 137, 16, 0.1)",
                                                transition: "background-color 0.3s ease",
                                            },
                                        }}
                                    >
                                        <TableCell>{ogrenci.no}</TableCell>
                                        <TableCell>{ogrenci.adiSoyadi}</TableCell>
                                        <TableCell>{ogrenci.sinif}</TableCell>
                                        <TableCell>{ogrenci.kayitTarihi}</TableCell>
                                        <TableCell>{ogrenci.sezon}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Tooltip title="Ödeme İşlemleri">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleOpenOdemeDialog(ogrenci)}
                                                        size="small"
                                                    >
                                                        <PaymentIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                {/* <Tooltip title="Taksit Planı">
                                                    <IconButton
                                                        color="secondary"
                                                        onClick={() => handleOpenTaksitPlanDialog(ogrenci)}
                                                        size="small"
                                                    >
                                                        <AccountBalanceWalletIcon />
                                                    </IconButton>
                                                </Tooltip> */}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    {/* Kayıt sayısı bilgisi */}
                    <Typography variant="body2" sx={{ mt: 1, textAlign: 'right', color: 'text.secondary' }}>
                        Toplam {filteredData.length} öğrenci listeleniyor {searchText && `(Filtre: ${searchText})`}
                    </Typography>
                </Box>
            )}
            
            {/* Ödeme Dialog */}
            {selectedOgrenci && (
                <OdemeDialogModal
                    open={odemeDialogOpen}
                    onClose={handleCloseOdemeDialog}
                    ogrenci={selectedOgrenci}
                    sezon={selectedSezon}
                />
            )}
            
            {/* Taksit Planı Dialog */}
            {selectedOgrenci && (
                <TaksitPlanDialogModal
                    open={taksitPlanDialogOpen}
                    onClose={handleCloseTaksitPlanDialog}
                    ogrenci={selectedOgrenci}
                    sezon={selectedSezon}
                />
            )}
            
            {/* Öğrenci Ekleme Dialog */}
            <OgrenciEkleDialogModal
                open={ogrenciEkleDialogOpen}
                onClose={handleCloseOgrenciEkleDialog}
                sezon={selectedSezon}
            />
        </Box>
    );
};

export default OgrenciMuhasebeIslemleri;